import { Link } from "react-router-dom";
import { useState } from "react";
import { ChevronDown, ChevronRight, ArrowUpRight, Code2, Smartphone, Cloud, BrainCircuit, Sparkles, Compass, Server, Wrench } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const DETAILED = [
  {
    icon: Code2,
    name: "Custom Software Development",
    body: "We build enterprise-grade custom software tailored to your unique business needs. From concept to deployment, our team delivers scalable, secure, and maintainable solutions that earn their keep.",
    features: ["Enterprise application development", "SaaS platform creation", "REST & GraphQL APIs", "Legacy modernization", "Microservices architecture", "Database design & optimization"],
    tech: ["Node.js", "Python", ".NET", "Go", "Java", "PostgreSQL", "MongoDB"],
    price: "From $25,000",
  },
  {
    icon: Smartphone,
    name: "Web & Mobile Applications",
    body: "Modern, performant apps built with the latest frameworks. Responsive, accessible, and SEO-optimized — designed to convert visitors and retain users.",
    features: ["React / Next.js apps", "iOS Native (Swift)", "Android Native (Kotlin)", "React Native & Flutter", "Progressive Web Apps", "Server-side rendering"],
    tech: ["React", "Next.js", "Vue", "Tailwind", "TypeScript", "Vercel"],
    price: "From $15,000",
  },
  {
    icon: Cloud,
    name: "Cloud Solutions & DevOps",
    body: "Scalable, secure cloud infrastructure that grows with your business. We design, migrate, and manage cloud environments across AWS, Azure, and GCP.",
    features: ["Cloud migration", "Infrastructure as Code", "Serverless architecture", "Container orchestration", "Managed services", "FinOps & cost optimization"],
    tech: ["AWS", "Azure", "GCP", "Docker", "Kubernetes", "Terraform", "Datadog"],
    price: "From $20,000",
  },
  {
    icon: BrainCircuit,
    name: "AI & Machine Learning",
    body: "Harness AI to automate processes, gain insights, and create intelligent UX. From predictive models to LLM integrations, we make AI useful.",
    features: ["Predictive analytics", "Natural language processing", "Computer vision", "LLM integration (GPT, Claude)", "Recommendation systems", "Custom model training"],
    tech: ["Python", "TensorFlow", "PyTorch", "OpenAI", "LangChain", "Hugging Face"],
    price: "From $35,000",
  },
  {
    icon: Wrench,
    name: "DevOps & Automation",
    body: "Streamline development with automated CI/CD pipelines, infrastructure automation, and observability that ensures reliability and speed.",
    features: ["CI/CD pipeline setup", "Container orchestration", "Monitoring & logging", "Security automation", "Infrastructure as Code", "Incident response"],
    tech: ["GitHub Actions", "Jenkins", "Docker", "Kubernetes", "Terraform", "Prometheus", "Grafana"],
    price: "From $15,000",
  },
  {
    icon: Sparkles,
    name: "UI/UX Design",
    body: "User-centered design that combines beauty with usability. Research, testing, and design systems that scale across teams and products.",
    features: ["User research & personas", "Wireframing & IA", "High-fidelity design", "Interactive prototypes", "Design systems", "Usability testing"],
    tech: ["Figma", "Sketch", "Framer", "Principle", "Adobe XD"],
    price: "From $10,000",
  },
  {
    icon: Server,
    name: "Cloud Migration & Modernization",
    body: "Move from legacy infrastructure to modern, scalable cloud platforms — without disrupting operations.",
    features: ["Lift & shift migration", "Re-architecture", "Zero-downtime cutover", "Multi-region setup", "Data migration", "Post-migration support"],
    tech: ["AWS", "Azure", "GCP", "Kubernetes", "Terraform"],
    price: "From $30,000",
  },
  {
    icon: Compass,
    name: "Technology Consulting",
    body: "Strategic guidance to align your tech stack with business goals. We audit, plan, and advise on architecture, hiring, and digital transformation.",
    features: ["Digital transformation", "Tech stack audits", "Solution architecture", "Team augmentation", "Vendor selection", "CTO-as-a-Service"],
    tech: ["Varies by engagement"],
    price: "From $5,000/mo",
  },
];

const FAQS = [
  {
    q: "What is your typical development process?",
    a: "Discovery (1–2 wks), Design (2–3 wks), Build (6–12 wks in two-week sprints), Test (2–3 wks), then Launch & Support. Every sprint includes a demo and a working increment in your hands.",
  },
  {
    q: "How do you price projects?",
    a: "Fixed-scope projects are quoted from a discovery phase. For longer engagements we offer time-and-materials with a clear weekly cap. Either way, you'll see a written estimate before any contract.",
  },
  {
    q: "Do you provide ongoing support?",
    a: "Yes. Post-launch we offer SLA-backed support, monitoring, incident response, and an option for a continuous improvement retainer.",
  },
  {
    q: "Can you work with our existing team?",
    a: "Absolutely. About 40% of our engagements are embedded — we plug into your stand-ups, code review, and Linear/Jira boards from day one.",
  },
  {
    q: "What technologies do you specialize in?",
    a: "Modern React/Next.js on the front, Node/Python/Go on the back. AWS/GCP/Azure for cloud. Strong AI/ML practice with OpenAI, LangChain and custom model work.",
  },
  {
    q: "Do you sign NDAs and own IP transfer?",
    a: "Yes to both. Standard mutual NDA up-front; full IP transfer on payment of the milestone tied to that work. We can also adapt to your paper.",
  },
];

export default function Services() {
  const [open, setOpen] = useState(null);

  return (
    <div data-testid="services-page" className="pt-32 pb-20">
      {/* Hero */}
      <section className="relative mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-20">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · Services
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Comprehensive <span className="text-gradient">technology solutions</span>.
          </h1>
          <p className="mt-7 text-xl text-zinc-400 max-w-2xl">
            End-to-end services from strategy and design to deployment and the years that follow.
          </p>
        </Reveal>
      </section>

      {/* Detailed grid (zigzag) */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 space-y-24 mt-10">
        {DETAILED.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.name}>
              <article
                data-testid={`service-detail-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="relative aspect-[5/4] rounded-3xl glass border-white/[0.08] grid place-items-center overflow-hidden grain mesh-bg">
                  <div className="relative">
                    <div className="absolute -inset-10 rounded-full bg-nexus-cyan/15 blur-3xl" />
                    <div className="relative h-24 w-24 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 grid place-items-center">
                      <Icon className="h-10 w-10 text-nexus-cyan" />
                    </div>
                  </div>
                  <span className="absolute top-5 left-5 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                    0{i + 1} / 0{DETAILED.length}
                  </span>
                </div>

                <div>
                  <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                    {s.name}
                  </h2>
                  <p className="mt-5 text-zinc-400 text-lg leading-relaxed">{s.body}</p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {s.features.map((f) => (
                      <li key={f} className="text-sm text-zinc-300 flex items-center gap-2">
                        <ChevronRight className="h-3.5 w-3.5 text-nexus-cyan" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {s.tech.map((t) => (
                      <span key={t} className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 rounded-full glass border-white/10 px-3 py-1.5">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-7 flex flex-wrap items-center gap-4">
                    <span className="text-sm text-zinc-500">
                      <span className="font-mono text-foreground">{s.price}</span>
                    </span>
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-5 py-2.5 text-sm hover:brightness-110 transition"
                    >
                      Start the conversation <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="mx-auto max-w-4xl px-6 md:px-12 mt-32">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ FAQ ]</p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight">
            Questions we get <span className="text-gradient">often</span>.
          </h2>
        </Reveal>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} className="border-b border-white/[0.06]">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-lg font-cabinet font-medium py-6 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-zinc-400 text-base leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12 rounded-2xl glass border-white/[0.08] p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl">Still have questions?</h3>
            <p className="text-zinc-400 text-sm mt-1">Get a written response within one business day.</p>
          </div>
          <Link to="/contact" className="rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-5 py-2.5 text-sm">
            Contact us
          </Link>
        </div>
      </section>
    </div>
  );
}
